<template>
    <div class="muven-container">
        <div class="muven-router-name-container">
            <p class="muven-router-name">Detalhes do pedido</p>
        </div>

        <div class="muven-content">
            <div class="modal" tabindex="-1" role="dialog" id="muven-order-xml-modal">
                <div class="modal-dialog" role="document">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Arquivo XML da nota fiscal</h5>                            
                        </div>
                        <div class="modal-body">
                            <div class="row">
                                <div class="col-12">
                                    <div class="muven-tip muven-tip-standalone tip-info mb-3">
                                        <fa icon="info" class="tip-icon" />
                                        <p>Ao adicionar o arquivo XML ao faturamento manualmente, as informações de série, número e chave de acesso da nota serão preenchidas automaticamente.</p>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-12 form-group">
                                    <label>Escolha o arquivo XML</label>
                                    <input type="file" 
                                        id="muven-order-xml-input"
                                        class="form-control" 
                                        accept=".xml"
                                        @change="fileChosenHandler" />
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" 
                                class="btn btn-primary" 
                                @click="addXmlToOrder(order, orderXml)"
                                :disabled="!orderXml.hasXml() || submittingOrderXml">
                                    Adicionar
                                    <Loading v-show="submittingOrderXml" />
                            </button>

                            <button type="button" 
                                class="btn btn-outline-secondary" 
                                @click="closeOrderXmlModal()">Fechar</button>
                        </div>
                    </div>
                </div>
            </div>

            <Breadcrumb :items="breadcrumbList" class="mb-3"/>

            <div class="" v-if="!loading && !loadError">
                <button class="btn btn-outline-secondary mr-3 mb-3"
                    v-if="order.canObtainInfoFromChannel()"                                                
                    :disabled="forcingRetrySync"
                    @click="forceRetrySync(order)">
                    
                    Obter atualização do canal

                    <Loading v-show="forcingRetrySync" />
                </button>

                <button class="btn btn-outline-secondary mr-3 mb-3"
                    v-if="order.canForceSync()"                                                
                    :disabled="forcingChannelSync"
                    @click="forceChannelSync(order)">
                    
                    Forçar atualização no canal

                    <Loading v-show="forcingChannelSync" />
                </button>

                <a class="btn btn-outline-secondary mb-3" 
                    :href="getAuditUrl(order.id, EntityEnum.Order)" target="_blank">
                    Ir para auditoria
                    <fa icon="external-link-alt" class="muven-external-link ml-1" />
                </a>
            </div>

            <Loading page v-if="loading" />            

            <ResourceNotFound
                v-if="!loading && loadError"
                resource="pedido"
                :identifier="$route.params.id"
                list-path="/pedidos"
                list-label="Voltar para pedidos"
                :kind="loadErrorKind"
                :error-message="loadErrorMessage"
                @retry="load" />

            <div class="muven-column-layout" v-if="!loading && !loadError">
                <div class="muven-column-left">
                    <div class="muven-card order-card-products">
                        <div class="muven-card-title">
                            <p>Produtos</p>
                            <small class="text-muted">{{orderItemsDescription}}</small>
                        </div>

                        <div class="order-items-container">
                            <table class="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th class="text-right">Preço</th>
                                        <th class="text-right">Qtd</th>
                                        <th class="text-right">Desc</th>
                                        <th class="text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in order.items" :key="index" class="order-item">
                                        <td>
                                            <p class="product-name">{{item.product.name}}</p>
                                            <p class="product-variation" v-show="item.product.isSku">{{item.product.getVariationsStringified()}}</p>
                                            <p class="product-sku">SKU: {{item.product.skuCode}}</p>
                                            <p class="product-sku">Código retaguarda: {{item.product.codeSource | infoOrPlaceholder}}</p>
                                        </td>
                                        <td class="text-right">{{item.price | currency}}</td>
                                        <td class="text-right">{{item.amount}}</td>
                                        <td class="text-right">{{item.getDiscountValue() | currency}}</td>
                                        <td class="text-right">{{item.getTotal() | currency}}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="muven-card mb-3">
                        <div class="muven-card-title">
                            <p>Cliente</p>
                        </div>

                        <div class="row" v-show="order.customer.hasNaturalInfo()">
                            <div class="col-12 col-md-7 mb-3">
                                <label>Nome</label>
                                <p>{{order.customer.fullName()}}</p>
                            </div>

                            <div class="col-12 col-md-5 mb-3">
                                <label>Telefone</label>
                                <p>{{order.customer.phone | infoOrPlaceholder}}</p>
                            </div>
                        </div>

                        <div class="row" v-show="order.customer.hasLegalInfo()">
                            <div class="col-12 col-md-7 mb-3">
                                <label>Nome empresa</label>
                                <p>{{order.customer.corporateName | infoOrPlaceholder}}</p>
                            </div>

                            <div class="col-12 col-md-5 mb-3">
                                <label>Telefone empresa</label>
                                <p>{{order.customer.corporatePhone | infoOrPlaceholder}}</p>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-12 col-md-7">
                                <label>Email</label>
                                <p>{{order.customer.email | infoOrPlaceholder}}</p>
                            </div>

                            <div class="col-12 col-md-5">
                                <label>
                                    <span v-show="!order.customer.isLegal()">CPF</span>
                                    <span v-show="order.customer.isLegal()">CNPJ</span>
                                </label>
                                <p>{{order.customer.document | infoOrPlaceholder}}</p>
                            </div>
                        </div>
                    </div>

                    <div class="muven-card mb-3">
                        <div class="muven-card-title">
                            <p>Endereço de entrega</p>
                        </div>

                        <div class="">
                            <div class="row">
                                <div class="col-12 col-md-4 mb-3">
                                    <label>CEP</label>
                                    <p class="mb-0">{{order.getShippingAddress().postalCode | infoOrPlaceholder}}</p>
                                </div>

                                <div class="col-12 col-md-4 mb-3">
                                    <label>Logradouro</label>
                                    <p class="mb-0"><span>{{order.getShippingAddress().street}}</span><span>, {{order.getShippingAddress().number}}</span></p>
                                </div>

                                <div class="col-12 col-md-4 mb-3">
                                    <label>Bairro</label>
                                    <p class="mb-0">{{order.getShippingAddress().neighborhood | infoOrPlaceholder}}</p>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-12 col-md-4 mb-3">
                                    <label>Complemento</label>
                                    <p class="mb-0">{{order.getShippingAddress().complement | infoOrPlaceholder}}</p>
                                </div>

                                <div class="col-12 col-md-4 mb-3">
                                    <label>Cidade</label>
                                    <p class="mb-0"><span>{{order.getShippingAddress().city}}</span>/<span>{{order.getShippingAddress().state}}</span></p>
                                </div>

                                <div class="col-12 col-md-4 mb-3">
                                    <label>Referência</label>
                                    <p class="mb-0">{{order.getShippingAddress().reference | infoOrPlaceholder}}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="muven-card mb-3">
                        <div class="muven-card-title">
                            <p>Endereço de cobrança</p>
                        </div>
                        
                        <div class="">
                            <div class="row">
                                <div class="col-12 col-md-4 mb-3">
                                    <label>CEP</label>
                                    <p class="mb-0">{{order.getBillingAddress().postalCode | infoOrPlaceholder}}</p>
                                </div>

                                <div class="col-12 col-md-4 mb-3">
                                    <label>Logradouro</label>
                                    <p class="mb-0"><span>{{order.getBillingAddress().street}}</span><span>, {{order.getBillingAddress().number}}</span></p>
                                </div>

                                <div class="col-12 col-md-4 mb-3">
                                    <label>Bairro</label>
                                    <p class="mb-0">{{order.getBillingAddress().neighborhood | infoOrPlaceholder}}</p>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-12 col-md-4 mb-3">
                                    <label>Complemento</label>
                                    <p class="mb-0">{{order.getBillingAddress().complement | infoOrPlaceholder}}</p>
                                </div>

                                <div class="col-12 col-md-4 mb-3">
                                    <label>Cidade</label>
                                    <p class="mb-0"><span>{{order.getBillingAddress().city}}</span>/<span>{{order.getBillingAddress().state}}</span></p>
                                </div>

                                <div class="col-12 col-md-4 mb-3">
                                    <label>Referência</label>
                                    <p class="mb-0">{{order.getBillingAddress().reference | infoOrPlaceholder}}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="muven-card mb-3">
                        <div class="muven-card-title">
                            <p>Dados da entrega</p>
                        </div>

                        <div class="row">
                            <div class="col-12 col-md-6 mb-3">
                                <label>Transportadora</label>
                                <p class="mb-0">{{order.getDeliveryCompany().name | infoOrPlaceholder}}</p>
                            </div>

                            <div class="col-12 col-md-6 mb-3">
                                <label>Valor do frete</label>
                                <p class="mb-0">{{order.getInvoice().freightTotal | currency | infoOrPlaceholder}}</p>
                            </div>

                            <div class="col-12 col-md-6 mb-3">
                                <label>Código de rastreio</label>
                                <p class="mb-0">{{order.getInvoice().trackingCode | infoOrPlaceholder}}</p>
                            </div>

                            <div class="col-12 col-md-6 mb-3">
                                <label>Url para rastreio</label>
                                <p class="mb-0">
                                    <a :href="order.getInvoice().trackingUrl"
                                        v-if="order.getInvoice().hasTrackingUrl()"
                                        target="_blank">Clique aqui</a>

                                    <span v-if="!order.getInvoice().hasTrackingUrl()">-</span>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="muven-card mb-3">
                        <div class="muven-card-title">
                            <p>Observações</p>
                        </div>

                        <div class="row">
                            <div class="col-12 mb-0">
                                <p v-for="(sellerNote, index) in order.getSellerNotesAsList()" :key="index" class="mb-3">
                                    {{sellerNote | infoOrPlaceholder}}
                                </p>
                                <p v-if="!order.hasSellerNotes()">-</p>
                            </div>
                        </div>
                    </div>

                    <div class="muven-card mb-3">
                        <div class="muven-card-title">
                            <p>Faturamento</p>
                        </div>

                        <div class="table-responsive">
                            <table class="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Chave de acesso</th>
                                        <th>Número</th>
                                        <th>Série</th>
                                        <th class="text-right">Ação</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    <tr v-for="(orderInvoice, index) in order.invoices" :key="index">
                                        <td>{{orderInvoice.accessKey | infoOrPlaceholder}}</td>
                                        <td>{{orderInvoice.invoiceNumber | infoOrPlaceholder}}</td>
                                        <td>{{orderInvoice.invoiceSeries | infoOrPlaceholder}}</td>
                                        <td>
                                            <button class="btn btn-sm btn-outline-secondary float-right"
                                                :disabled="orderInvoice.loadingListAction"
                                                v-show="orderInvoice.hasXmlUrl()"
                                                @click="downloadXml(orderInvoice)">
                                                Baixar XML

                                                <fa icon="file-download" v-show="!orderInvoice.loadingListAction" />
                                                <Loading v-show="orderInvoice.loadingListAction" />
                                            </button>

                                            <button class="btn btn-outline-secondary btn-sm float-right"
                                                v-show="order.canAddXml()"
                                                @click="openOrderXmlModal(orderInvoice)">
                                                
                                                Adicionar XML
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div class="muven-column-right">
                    <div class="muven-card mb-3">
                        <div class="muven-card-title">
                            <p>Status</p>
                        </div>

                        <div>
                            <p>{{order.getOrderStatusEnumDescription()}}</p>
                        </div>
                    </div> 

                    <div class="muven-card mb-3">
                        <div class="muven-card-title">
                            <p>Totais</p>
                        </div>

                        <div>
                            <div class="row">
                                <div class="col-12 mb-3">
                                    <label>Subtotal</label>
                                    <p>{{order.getItemsTotal() | currency}}</p>
                                </div>

                                <div class="col-12 mb-3">
                                    <label>Desconto</label>
                                    <p>{{order.discountValue | currency}}</p>
                                </div>

                                <div class="col-12 mb-3">
                                    <label>Taxa</label>
                                    <p>{{order.taxesValue | currency}}</p>
                                </div>

                                <div class="col-12 mb-3">
                                    <label>Acréscimo</label>
                                    <p>{{order.incrementValue | currency}}</p>
                                </div>

                                <div class="col-12 mb-3">
                                    <label>Frete</label>
                                    <p>{{order.getFreightTotal() | currency}}</p>
                                </div>

                                <div class="col-12">
                                    <label>Total</label>
                                    <p>{{order.getTotal() | currency}}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="muven-card mb-3">
                        <div class="muven-card-title">
                            <p>Pagamento</p>
                        </div>

                        <div class="row" v-for="(orderPayment, index) in order.payments" :key="index"
                            :class="{'mb-3': order.hasMoreThanOnePayment()}">
                            <div class="col-12">
                                <div class="form-group">
                                    <label>Nome</label>
                                    <p>{{orderPayment.payment.name | infoOrPlaceholder}}</p>
                                </div>
                            </div>

                            <div class="col-12" v-show="orderPayment.hasInstallments()">
                                <div class="form-group">
                                    <label>Parcelas</label>
                                    <p>{{orderPayment.installments | infoOrPlaceholder}}x</p>
                                </div>
                            </div>

                            <div class="col-12" v-show="orderPayment.hasNsuCode()">
                                <div class="form-group">
                                    <label for="">Código NSU</label>
                                    <p>{{orderPayment.nsuCode | infoOrPlaceholder}}</p>
                                </div>
                            </div>

                            <div class="col-12" v-show="orderPayment.hasAuthorizationNumber()">
                                <div class="form-group">
                                    <label for="">Código de autorização</label>
                                    <p>{{orderPayment.authorizationNumber | infoOrPlaceholder}}</p>
                                </div>
                            </div>

                            <div class="col-12" v-show="orderPayment.hasDetails()">
                                <div class="form-group">
                                    <label for="">Detalhes</label>
                                    <p>{{orderPayment.getDetails() | infoOrPlaceholder}}</p>
                                </div>
                            </div>

                            <div class="col-12">
                                <div class="form-group">
                                    <label>Total</label>
                                    <p>{{orderPayment.value | currency}}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="muven-card mb-3">
                        <div class="muven-card-title">
                            <p>Integração</p>
                        </div>

                        <div class="row">
                            <div class="form-group col-6">
                                <label>Canal</label>
                                <p>{{order.channel.name}}</p>
                            </div>

                            <div class="form-group col-6">
                                <label>Id canal</label>
                                <p>{{order.idExternalChannel | infoOrPlaceholder}}</p>
                            </div>

                            <div class="form-group col-6">
                                <label>Momento venda</label>
                                <p>{{order.createdAt | dateTimeFilter}}</p>
                            </div>

                            <div class="form-group col-6">
                                <label>Id retaguarda</label>
                                <p>{{order.idSource | infoOrPlaceholder}}</p>
                            </div>

                            <div class="form-group col-6">
                                <label>Id Muven</label>
                                <p>{{order.id}}</p>
                            </div>

                            <div class="form-group col-6">
                                <label>Cód. retaguarda</label>
                                <p>{{order.codeSource | infoOrPlaceholder}}</p>
                            </div>

                            <div class="form-group col-6">
                                <label>Cód. Loja</label>
                                <p>{{order.store.idSource | infoOrPlaceholder}}</p>
                            </div>

                            <div class="form-group col-6">
                                <label>Modificado por</label>
                                <p>{{order.getIntegrationSourceEnumDescription() | infoOrPlaceholder}}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import {dateTimeFilter} from '@/utils/filters/DateTimeFilter'
import {infoOrPlaceholder} from '@/utils/filters/InformationOrPlaceholderFilter'
import {OrderModel} from '@/order/models/OrderModel'
import {OrderXmlModel} from '@/order/models/OrderXmlModel'
import orderService from '@/order/services/OrderService'
import { EntityEnum } from '@c2b/muven-commons'
import GetAuditUrl from '@/utils/mixins/GetAuditUrl'
import productService from '@/product/services/ProductService'
import { fileToString } from '../../utils/helpers/FileHelper'
import ModalService from '../../utils/services/ModalService'
import ResourceLoadError from '../../utils/mixins/ResourceLoadError'

const ID_ORDER_XML_MODAL = '#muven-order-xml-modal'

export default {
    name: 'OrderView',    
    data() {
        return {
            EntityEnum,
            order: new OrderModel(),
            orderXml: new OrderXmlModel(),
            loading: true,
            forcingRetrySync: false,
            forcingChannelSync: false,
            submittingOrderXml: false,
            breadcrumbList: [
                {
                    id: 0,
                    description: 'Pedidos',
                    path: '/pedidos',
                },
                {
                    id: 1,
                    description: `Detalhes de #${this.$route.params.id}`,
                    path: '',
                },
            ],
        }
    },
    filters: {
        dateTimeFilter,
        infoOrPlaceholder,
    },
    mixins: [
        GetAuditUrl,
        ResourceLoadError,
    ],
    methods: {
        async load() {
            this.loading = true
            this.loadError = null

            try {
                this.order = await this._getCompleteOrder()
            } catch(e) {
                this.loadError = e
            } finally {
                this.loading = false
            }
        },
        async downloadXml(invoice) {
            invoice.loadingListAction = true

            try {
                const xml = await orderService.getOrderInvoiceXml(invoice)
                const pom = document.createElement('a')
                const blob = new Blob([xml], {type: 'text/plain'})

                pom.setAttribute('href', window.URL.createObjectURL(blob));
                pom.setAttribute('download', `${invoice.accessKey}.xml`);
                pom.dataset.downloadurl = ['text/plain', pom.download, pom.href].join(':');
                pom.click();
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message
                })
            }

            invoice.loadingListAction = false
        },
        async forceRetrySync(order) {
            this.forcingRetrySync = true

            try {
                const info = await orderService.retrySync(order.id)
                
                this.$floatingAlert.info({
                    message: info.message,
                })
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.forcingRetrySync = false
        },
        async forceChannelSync(order) {
            this.forcingChannelSync = true

            try {
                const info = await orderService.forceChannelSync(order.id)
                
                this.$floatingAlert.info({
                    message: info.message,
                })
            } catch(e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.forcingChannelSync = false
        },
        async addXmlToOrder(order, orderXml) {
            this.submittingOrderXml = true

            try {
                await orderService.addXml(order.id, orderXml)
                this.order = await this._getCompleteOrder() 
                this.closeOrderXmlModal()

                this.$floatingAlert.success({
                    message: 'XML da nota adicionado com sucesso ao pedido.',
                })                
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }

            this.submittingOrderXml = false
        },
        openOrderXmlModal(orderInvoice) {
            document.getElementById('muven-order-xml-input').value = null

            this.orderXml = new OrderXmlModel({
                invoiceId: orderInvoice.id,
                xml: '',
            })

            ModalService.show(ID_ORDER_XML_MODAL)
        },
        closeOrderXmlModal() {
            ModalService.hide(ID_ORDER_XML_MODAL)
        },
        async fileChosenHandler({target}) {
            try {
                this.orderXml.setXml(await fileToString(target.files[0]));
            } catch (err) {
                this.$floatingAlert.error({
                    message: err.message,
                })
            }
        },
        async _getCompleteOrder() {
            const order = await orderService.getOrderById(this.$route.params.id)

            for (const item of order.items) {
                if (item.product.isSku) {
                    item.product = await productService.getProductById(item.product.id)
                }
            }

            return order
        }
    },
    mounted() {
        this.load()
    },
    computed: {
        ...mapState({
            subscriber: state => state.subscriber,
        }),
        orderItemsDescription() {
            if (this.order.items.length > 1) {
                return `${this.order.items.length} itens`
            }

            return `1 item`
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../utils/styles/constants.scss";
.muven-card {
    &.order-card-products {
        margin-bottom: 15px;
        padding-bottom: 0;

        .order-items-container {
            max-height: 390px;
            overflow-y: auto;

            .order-item {
                .product-name,
                .product-variation,
                .product-sku {
                    margin-bottom: 0;
                }

                .product-variation,
                .product-sku {
                    color: var(--text-muted);
                    font-size: .9em;
                }
            }
        }
    }
}
</style>